import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";

import EventCategoriesApi from "../../rrhh/eventos/api/eventCategoriesApi";
import { DEFAULT_EVENT_CATEGORY_COLOR } from "../../rrhh/eventos/constants/eventCategories";

const INITIAL_FORM = {
  name: "",
  color: DEFAULT_EVENT_CATEGORY_COLOR,
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: "rgba(255,255,255,0.85)",
    fontSize: "14px",
    fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
    "& fieldset": {
      borderColor: "rgba(40, 80, 160, 0.3)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(40, 80, 160, 0.6)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(14, 50, 149, 1)",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
    fontSize: "13px",
    fontWeight: 600,
    color: "rgba(2, 8, 23, 0.7)",
  },
  "& .MuiFormHelperText-root": {
    ml: 0.5,
    mt: 0.6,
    fontSize: "12px",
    fontWeight: 600,
  },
};

function StyledField(props) {
  return (
    <TextField
      {...props}
      fullWidth
      size="small"
      variant="outlined"
      sx={fieldSx}
    />
  );
}

function normalizeColor(value) {
  const color = String(value || "").trim().toLowerCase();
  return /^#[0-9a-f]{6}$/.test(color) ? color : DEFAULT_EVENT_CATEGORY_COLOR;
}

function categoryToForm(category) {
  if (!category) return INITIAL_FORM;
  return {
    name: category.name || "",
    color: normalizeColor(category.color),
  };
}

function normalizeForm(form) {
  return {
    name: String(form.name || "").trim(),
    color: normalizeColor(form.color),
  };
}

export default function EventCategoriesConfigPanel() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await EventCategoriesApi.list();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("event categories", error);
      setCategories([]);
      setToast({
        open: true,
        severity: "error",
        message: error?.message || "No se pudieron cargar las categorías.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const selectedCategory = useMemo(
    () =>
      categories.find(
        (category) => Number(category.id) === Number(selectedCategoryId)
      ) || null,
    [categories, selectedCategoryId]
  );

  const isEditing = Boolean(selectedCategory);

  const initialEditForm = useMemo(
    () => categoryToForm(selectedCategory),
    [selectedCategory]
  );

  useEffect(() => {
    setForm(categoryToForm(selectedCategory));
    setFormErrors({});
  }, [selectedCategory]);

  const hasEditChanges = useMemo(() => {
    if (!isEditing) return false;
    const current = normalizeForm(form);
    const initial = normalizeForm(initialEditForm);
    return current.name !== initial.name || current.color !== initial.color;
  }, [form, initialEditForm, isEditing]);

  const canCreate = useMemo(() => {
    return String(form.name || "").trim().length > 0 && !saving;
  }, [form.name, saving]);

  const handleSelectCategory = (categoryId) => {
    setSelectedCategoryId((prev) =>
      Number(prev) === Number(categoryId) ? null : Number(categoryId)
    );
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setSelectedCategoryId(null);
    setForm(INITIAL_FORM);
    setFormErrors({});
  };

  const validateForm = () => {
    const nextErrors = {};
    const normalized = normalizeForm(form);

    if (!normalized.name) nextErrors.name = "Ingresá un nombre";
    if (!/^#[0-9a-f]{6}$/.test(normalized.color)) {
      nextErrors.color = "Usá un color hexadecimal válido";
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateForm();
    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const payload = normalizeForm(form);
    setSaving(true);

    try {
      if (isEditing && selectedCategoryId) {
        await EventCategoriesApi.update(selectedCategoryId, payload);
        setToast({
          open: true,
          severity: "success",
          message: "Categoría actualizada correctamente.",
        });
      } else {
        await EventCategoriesApi.create(payload);
        setToast({
          open: true,
          severity: "success",
          message: "Categoría creada correctamente.",
        });
      }

      resetForm();
      await loadCategories();
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message:
          error?.message ||
          (isEditing
            ? "No se pudo actualizar la categoría."
            : "No se pudo crear la categoría."),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;

    const confirmed = window.confirm(
      `¿Querés borrar la categoría "${selectedCategory.name}"?`
    );

    if (!confirmed) return;

    setSaving(true);
    try {
      await EventCategoriesApi.remove(selectedCategory.id);
      setToast({
        open: true,
        severity: "success",
        message: "Categoría borrada correctamente.",
      });
      resetForm();
      await loadCategories();
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error?.message || "No se pudo borrar la categoría.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Paper
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: "24px",
          bgcolor: "neutral.200",
          boxShadow: "inset 0px 0px 6px rgba(64,64,94,0.25)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 900, color: "primary.900" }}>
              Categorías de eventos
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "neutral.700", mt: 0.5 }}>
              Administrá las categorías disponibles para nuevos eventos.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadCategories}
            disabled={loading || saving}
            sx={{
              alignSelf: { xs: "flex-start", md: "center" },
              borderRadius: "33px",
              px: 2.5,
              py: 1,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Actualizar lista
          </Button>
        </Stack>

        <Stack spacing={3} sx={{ mt: 2 }}>
          <TableContainer
            component={Box}
            sx={{
              borderRadius: "24px",
              bgcolor: "neutral.50",
              boxShadow: "0 14px 36px rgba(15, 23, 42, 0.08)",
              maxHeight: "40vh",
              overflow: "auto",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900, bgcolor: "neutral.300" }}>Color</TableCell>
                  <TableCell sx={{ fontWeight: 900, bgcolor: "neutral.300" }}>Categoría</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : null}

                {!loading && categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 5, color: "neutral.700", fontWeight: 700 }}>
                      No hay categorías cargadas.
                    </TableCell>
                  </TableRow>
                ) : null}

                {!loading &&
                  categories.map((category) => {
                    const active = Number(category.id) === Number(selectedCategoryId);

                    return (
                      <TableRow
                        key={category.id}
                        hover
                        selected={active}
                        onClick={() => handleSelectCategory(category.id)}
                        sx={{
                          cursor: "pointer",
                          "&.Mui-selected": {
                            bgcolor: "rgba(14, 50, 149, 0.08)",
                          },
                        }}
                      >
                        <TableCell sx={{ width: 90 }}>
                          <Box
                            sx={{
                              width: 28,
                              height: 18,
                              borderRadius: "4px",
                              bgcolor: category.color,
                              border: "1px solid rgba(15, 23, 42, 0.16)",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: "primary.900" }}>
                          {category.name}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: "24px",
              bgcolor: "neutral.50",
              boxShadow: "0 14px 36px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: "primary.900", mb: 2 }}>
              {isEditing ? "Editar categoría" : "Agregar categoría"}
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(0, 1fr) 160px",
                  },
                }}
              >
                <StyledField
                  label="Nombre"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  required
                  error={Boolean(formErrors.name)}
                  helperText={formErrors.name}
                  placeholder="Ej: Boliche"
                />

                <StyledField
                  label="Color"
                  name="color"
                  type="color"
                  value={normalizeColor(form.color)}
                  onChange={handleFormChange}
                  required
                  error={Boolean(formErrors.color)}
                  helperText={formErrors.color}
                  inputProps={{ "aria-label": "Color de categoría" }}
                />
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ mt: 2 }}
              >
                <Box>
                  {isEditing ? (
                    <Button
                      onClick={handleDelete}
                      color="error"
                      variant="outlined"
                      disabled={saving || categories.length <= 1}
                      startIcon={<DeleteOutlineIcon />}
                      sx={{
                        borderRadius: "33px",
                        px: 3,
                        py: 1,
                        textTransform: "none",
                        fontWeight: 700,
                      }}
                    >
                      Borrar categoría
                    </Button>
                  ) : null}
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  <Button
                    onClick={resetForm}
                    color="secondary"
                    variant="outlined"
                    disabled={saving}
                    sx={{
                      borderRadius: "33px",
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Cancelar
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving || (isEditing ? !hasEditChanges : !canCreate)}
                    startIcon={<SaveIcon />}
                    sx={{
                      borderRadius: "33px",
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    {saving
                      ? "Guardando..."
                      : isEditing
                      ? "Guardar cambios"
                      : "Agregar categoría"}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
